import { FormEvent, useState } from 'react';
import useTableOrder from 'hooks/tableOrder';
import { Surgerie } from 'types/surgerie';
import { api } from 'services/api';
import { Paginated } from 'types/paginated';
import { formatDate, startOfDay, startOfMonth, subDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { moneyFormat } from 'helpers/moneyFormat';
import { useApp } from 'hooks/app';
import { checkDateRange } from 'helpers/dateRange';

export type SurgerieQueryParams = {
  initial_date: Date | null;
  final_date: Date | null;
  doctor_name: string;
  patient_name: string;
  status: string;
  hospital_name: string;
  created_by: string;
  closed_by: string;
  date_filter: 'created_at' | 'closed_at';
};

export function useFetchSurgeries() {
  const [surgeries, setSurgeries] = useState<Surgerie[]>([]);
  const [selectedSurgerie, setSelectedSurgerie] = useState<Surgerie | null>(null);
  const [loading, setLoading] = useState(false);
  const { isOpenedMenu, handleOpenMenu } = useApp();
  const [page, setPage] = useState<number>(0);
  const [rows, setRows] = useState<number>(96);
  const [total, setTotal] = useState<number>(0);
  const [, sort] = useTableOrder();
  const [filter, setFilter] = useState<SurgerieQueryParams>({
    doctor_name: '',
    patient_name: '',
    initial_date: startOfMonth(subDays(new Date(), 1)),
    final_date: new Date(),
    status: 'closed',
    hospital_name: '',
    created_by: '',
    closed_by: '',
    date_filter: 'created_at',
  });

  function fetchData(nextPage = page, nextRows = rows, nextFilter = filter) {
    if (!checkDateRange(nextFilter)) return;

    setLoading(true);
    if (isOpenedMenu) {
      handleOpenMenu();
    }

    api
      .get<Paginated<Surgerie[]>>('/budgets', {
        params: {
          ...nextFilter,
          page: nextPage + 1,
          rows: nextRows,
          initial_date: formatDate(nextFilter.initial_date, 'yyyy-MM-dd 00:00:00'),
          final_date: formatDate(nextFilter.final_date, 'yyyy-MM-dd 23:59:59'),
        },
      })
      .then(response => {
        setTotal(response.data.total);
        const _surgeries = response.data.items
          .map(surgerie => ({
            ...surgerie,
            formattedAnesthetistValue: moneyFormat(surgerie.value_anesthetist || 0),
            formattedDoctorValue: moneyFormat(surgerie.value_doctor || 0),
            formattedHospitalValue: moneyFormat(surgerie.value_hospital || 0),
            formattedPackagesValue: moneyFormat(surgerie.value_packages || 0),
            formattedBudgetValue: moneyFormat(surgerie.value_budget || 0),
            formattedTotalValue: moneyFormat(surgerie.value_total || 0),
            formattedMarkupValue: moneyFormat(surgerie.value_markup || 0),
            formattedValueEntry: moneyFormat(surgerie.value_entry || 0),
            formattedValuePaid: moneyFormat(surgerie.value_paid || 0),
            closed_at: surgerie.closed_at || '',
            formattedClosedAt: surgerie.closed_at ? formatDate(surgerie.closed_at, 'P', { locale: ptBR }) : '',
            formattedCreatedAt: formatDate(surgerie.created_at || new Date(), 'P', { locale: ptBR }),
            formattedDoctorName: surgerie.doctor?.doctor?.name || 'Não informado',
            formattedPatientName: surgerie.patient?.name || 'Não informado',
            formattedProcedureName: surgerie.procedure?.name || 'Não informado',
            formattedHospitalName: surgerie.hospital?.company_name || 'Não informado',
            formattedStatus: surgerie.status === 'open' ? 'Solicitação aberta' : 'Solicitação fechada',
          }))
          .filter(item => item.closed_by?.toLocaleUpperCase().includes(nextFilter.closed_by.toLocaleUpperCase()));
        setSurgeries(_surgeries);
      })
      .catch(err => toast.error(err.response.data.error))
      .finally(() => setLoading(false));
  }

  function handleSearch(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    setPage(0);
    fetchData(0, rows, filter);
  }

  function handleChangePage(nextPage: number) {
    setPage(nextPage);
    fetchData(nextPage, rows, filter);
  }

  function handleChangeRows(nextRows: number) {
    setRows(nextRows);
    setPage(0);
    fetchData(0, nextRows, filter);
  }

  function handleSort(index: string) {
    const p = sort(index, surgeries);
    setSurgeries(p);
  }

  function handleChangeFilter<K extends keyof SurgerieQueryParams>(index: K, value: SurgerieQueryParams[K]) {
    setFilter(state => ({
      ...state,
      [index]: value,
    }));
  }

  return {
    surgeries,
    loading,
    handleSort,
    filter,
    handleChangeFilter,
    setPage: handleChangePage,
    setRows: handleChangeRows,
    handleSearch,
    page,
    rows,
    total,
    selectedSurgerie,
    setSelectedSurgerie,
  };
}
